/**
 * Conservative detector for an explicit font directive typed into Webster chat, so we
 * can apply it instantly (via the apply-font endpoint) instead of spending an AI rebuild.
 *
 * Deliberately strict to avoid hijacking normal edit requests: it only fires when the
 * message mentions the word "font(s)". Anything ambiguous returns null and falls through
 * to normal Webster chat. Returns the heading/body families to apply (null clears a role
 * to the system fallback), or null to not intercept.
 */
export function detectFontDirective(message: string): { heading: string | null; body: string | null } | null {
    const text = message.trim()

    // Bare "use/switch to/change to/try <Capitalized Family>" — font names are proper
    // nouns, so requiring capitalization + a short message lets "use Poppins" through
    // while ignoring normal edits like "use bullet points" or "make the hero bigger".
    if (text.split(/\s+/).length <= 5) {
        const bare = text.match(/^(?:please\s+)?(?:use|switch\s+to|change\s+to|try)\s+["']?([A-Z][A-Za-z]+(?:\s[A-Z][A-Za-z]+){0,2})["']?\s*$/)
        if (bare) {
            const family = bare[1].trim()
            return { heading: family, body: family }
        }
    }

    // Otherwise, only messages that explicitly say "font"/"fonts" are considered.
    if (!/\bfonts?\b/i.test(text)) return null

    // "use our/the/default brand font(s)" / "revert the font" → clear the override.
    if (/\b(?:use|keep|restore|revert\s+to|go\s+back\s+to)\s+(?:our|the|my|default)\s+(?:brand\s+)?fonts?\b/i.test(text)) {
        return { heading: null, body: null }
    }

    // Family sits next to the word "font": "font to Poppins" / "font: Poppins" / "use Poppins font".
    const match =
        text.match(/\bfonts?\s*(?:to|=|:)?\s*["']?([A-Za-z][A-Za-z0-9 ]{1,28}?)["']?\s*$/i) ||
        text.match(/\b(?:use|try|switch\s+to|change\s+to|set)\s+["']?([A-Za-z][A-Za-z0-9 ]{1,28}?)["']?\s+fonts?\b/i)

    if (!match) return null

    const family = match[1].trim()
    const forHeading = /\b(?:heading|title|headline|display)s?\b/i.test(text)
    const forBody = /\b(?:body|paragraph|text)s?\b/i.test(text)

    if (forHeading && !forBody) return { heading: family, body: null }
    if (forBody && !forHeading) return { heading: null, body: family }
    return { heading: family, body: family }
}
